import { getDefaultOptions } from "../../services/Category";
import { simpleQuery } from "../../services/utils";

export type ForecastTarget = "nb_uh" | "adr";

/** How far a forecast misses, or which way it leans. Both in the target's own unit. */
export type ErrorKind = "mae" | "bias";

export interface LeadTimePoint {
  lead_time: number;
  n: number;
  mae: number;
  baseline_mae: number;
}

export interface ForecastMetrics {
  target: ForecastTarget;
  model_name: string;
  trained_at: string;
  training_status: "pending" | "running" | "done" | "error";
  /** Stage code of a run in progress, empty otherwise. See TRAINING_STEP_LABELS. */
  training_step: string;
  n_train: number;
  n_test: number;
  train_start_date: string | null;
  train_end_date: string | null;
  test_start_date: string | null;
  test_end_date: string | null;
  mae: number;
  rmse: number;
  wape: number;
  bias: number;
  r2: number;
  baseline_mae: number;
  baseline_rmse: number;
  baseline_wape: number;
  baseline_bias: number;
  baseline_r2: number;
  skill_score: number;
  features: string[];
  feature_importances: Record<string, number>;
  lead_time_curve: LeadTimePoint[];
}

export const fetchForecastMetrics = async (): Promise<ForecastMetrics[]> =>
  simpleQuery("/api/ml-forecast/metrics/");

export interface BacktestProperty {
  id: number;
  name: string;
}

export interface BacktestFilters {
  properties: BacktestProperty[];
  uh_types: BacktestProperty[];
  months: string[];
  date_min: string | null;
  date_max: string | null;
}

/** One point of the booking curve, aggregated over the filtered scope. */
export interface BacktestCurvePoint {
  lead_time: number;
  n: number;
  /** Already on the books at that lead time — the curve that climbs. */
  rob_nb_uh: number;
  rob_revenue: number;
  /** Realized total: identical at every lead time, the flat reference. */
  actual_nb_uh: number;
  actual_revenue: number;
  /** Landing forecast of each method at that lead time. The baseline sums are zero where the
   *  production procedure forecast nothing, so baseline_n says how many rows it covered. */
  baseline_nb_uh: number;
  baseline_revenue: number;
  baseline_n: number;
  baseline_adr_n: number;
  ml_nb_uh: number;
  ml_revenue: number;
  /**
   * Sum of the absolute errors of the individual rows behind this lead time, and the
   * denominator they divide by — `n` rows for room nights and revenue, the realized room
   * rows that carry a price. Kept separate from the totals above because the error of a sum
   * is not the sum of the errors: opposite misses cancel in the total.
   */
  baseline_abs_nb_uh: number;
  ml_abs_nb_uh: number;
  baseline_abs_revenue: number;
  ml_abs_revenue: number;
  baseline_abs_adr: number;
  ml_abs_adr: number;
  adr_n: number;
  /** The same sums kept signed: positive means the forecast sold more than reality did. */
  baseline_err_nb_uh: number;
  ml_err_nb_uh: number;
  baseline_err_revenue: number;
  ml_err_revenue: number;
  baseline_err_adr: number;
  ml_err_adr: number;
}

export interface BreakdownRow {
  key: string;
  label: string;
  n: number;
  /** Signed: positive means the forecast sells more than reality did. */
  baseline_bias: number;
  ml_bias: number;
  /** The same error in the unit of the measure: room nights, or euros. */
  baseline_mae: number;
  ml_mae: number;
}

export type BreakdownDimension =
  | "hebergement"
  | "modele_type"
  | "uh_type"
  | "lead_time"
  | "month"
  | "day_of_week";

/**
 * One read of the test period serves the whole page: the curve and every breakdown come from
 * the same rows, and both measures from the same buckets — so switching measure draws from
 * what is already loaded instead of asking again.
 */
export interface Backtest {
  curve: BacktestCurvePoint[];
  dimensions: Record<
    BreakdownDimension,
    Record<ForecastTarget, BreakdownRow[]>
  >;
}

export const fetchBacktest = async (params: {
  hebergementIds: number[];
  uhTypeIds: number[];
  month?: string;
  dateOccupation?: string;
}): Promise<Backtest> => {
  const query = new URLSearchParams();
  if (params.hebergementIds.length > 0) {
    query.set("hebergement_ids", params.hebergementIds.join(","));
  }
  if (params.uhTypeIds.length > 0) {
    query.set("uh_type_ids", params.uhTypeIds.join(","));
  }
  if (params.dateOccupation)
    query.set("date_occupation", params.dateOccupation);
  else if (params.month) query.set("month", params.month);
  return simpleQuery(`/api/ml-forecast/backtest/?${query.toString()}`);
};

export const fetchBacktestFilters = async (): Promise<BacktestFilters> =>
  simpleQuery("/api/ml-forecast/backtest-filters/");

export interface ForecastActivation {
  active: boolean;
}

export const fetchActivation = async (): Promise<ForecastActivation> =>
  simpleQuery("/api/ml-forecast/activation/");

export const setActivation = async (
  active: boolean
): Promise<ForecastActivation> => {
  const response = await fetch(
    "/api/ml-forecast/activation/",
    getDefaultOptions({ method: "PUT", body: JSON.stringify({ active }) })
  );
  if (!response.ok) throw new Error(String(response.status));
  return response.json();
};

export const trainForecastModels = async (): Promise<void> => {
  const response = await fetch(
    "/api/ml-forecast/train/",
    getDefaultOptions({ method: "POST", body: JSON.stringify({}) })
  );
  // The status alone: the body carries an English message meant for a developer, and the
  // page is the only place that knows the reader's language.
  if (!response.ok) throw new Error(String(response.status));
};
